/**
 * Icon registry context — lets consumers supply an icon-name → SVG-component
 * mapping without coupling a3s-ui to a specific set of SVG imports or bundler.
 *
 * The app wires this once at its root layout (alongside FontFamilyProvider).
 * Components like Icon, CircularIcon, and SimpleIcon read from this context
 * to resolve the SVG component for a given icon name.
 */
import { createContext, useContext, type FC, type ReactNode } from "react";
import type { IconRegistry } from "./IconRegistry";

const IconRegistryContext = createContext<IconRegistry | undefined>(undefined);

export interface IconProviderProps {
  readonly registry: IconRegistry;
  readonly children: ReactNode;
}

export const IconProvider: FC<IconProviderProps> = ({ registry, children }) => (
  <IconRegistryContext.Provider value={registry}>{children}</IconRegistryContext.Provider>
);

export function useIconRegistry(): IconRegistry {
  const registry = useContext(IconRegistryContext);
  if (registry === undefined) {
    throw new Error(
      "useIconRegistry must be used within an <IconProvider>. Wrap your app root with <IconProvider registry={...}>.",
    );
  }
  return registry;
}
